import React from 'react'
import { TrendingUp, Info } from 'lucide-react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { useCalculation } from '@/hooks/useCalculation'

interface KpiCardProps {
  label: string
  value: string | number
  secondary?: string
  color?: string
  darkMode: boolean
  onClick?: () => void
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, secondary, color, darkMode, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`p-3 md:p-5 rounded-xl md:rounded-2xl border transition-all duration-300 flex flex-col items-center text-center ${onClick ? 'cursor-pointer hover:ring-2 hover:ring-emerald-500/50 hover:-translate-y-0.5' : ''} ${
        darkMode
          ? 'bg-slate-900/40 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <h3
        className={`mb-2 text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] leading-relaxed ${
          darkMode ? 'text-slate-500' : 'text-slate-400'
        }`}
      >
        {label}
      </h3>
      <div className={`text-lg md:text-2xl font-black tracking-tight mb-1 ${color || (darkMode ? 'text-white' : 'text-slate-900')}`}>
        {typeof value === 'number' && !label.toLowerCase().includes('years') 
          ? `$${value.toLocaleString(undefined, { minimumFractionDigits: value % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}` 
          : value}
      </div>
      {secondary && (
        <div className={`text-[10px] font-bold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          {secondary}
        </div>
      )}
    </div>
  )
}

export const KpiSection: React.FC<{ hideHeader?: boolean }> = ({ hideHeader }) => {
  const darkMode = useEnergyStore((s) => s.darkMode)
  const config = useEnergyStore((s) => s.config)
  const userLevel = useEnergyStore((s) => s.userLevel)
  const setKpiModalOpen = useEnergyStore((s) => s.setKpiModalOpen)
  const { totals, averages, roi } = useCalculation()

  const showRoiCards = userLevel !== 'basic' && (userLevel === 'advanced' || config.showRoi)

  return (
    <div className="space-y-8">
      {!hideHeader && (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${darkMode ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
            <TrendingUp size={18} />
          </div>
          <h2 className="text-sm font-black uppercase tracking-widest">Economic Viability Summary</h2>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        <KpiCard
          label={
            config.useSolar !== false
              ? 'Cost Without Solar'
              : (config.useEv && config.includeEvInRoi ? 'ICE Baseline Cost' : 'Baseline Grid Cost')
          }
          value={Math.round(totals.costWithoutSolar + (config.includeEvInRoi ? (totals.evPetrolSavings ?? 0) : 0))}
          secondary={`Avg $${((totals.costWithoutSolar + (config.includeEvInRoi ? (totals.evPetrolSavings ?? 0) : 0)) / 12).toFixed(2)} / mo`}
          darkMode={darkMode}
          onClick={() => setKpiModalOpen('Cost Without Solar')}
        />
        <KpiCard
          label="Net Cost"
          value={Math.round(totals.netCost)}
          secondary={`Avg $${(totals.netCost / 12).toFixed(2)} / mo`}
          color="text-emerald-500"
          darkMode={darkMode}
          onClick={() => setKpiModalOpen('Net Cost')}
        />
        <KpiCard
          label={
            config.useSolar !== false
              ? config.useEv && config.includeEvInRoi
                ? 'Total Savings (Solar+EV)'
                : 'Solar Benefit'
              : config.useEv && config.includeEvInRoi
                ? 'EV Petrol Savings'
                : 'Total Savings'
          }
          value={Math.round(totals.solarBenefit + (totals.evPetrolSavings ?? 0))}
          secondary={`${(((totals.solarBenefit + (totals.evPetrolSavings ?? 0)) / ((totals.costWithoutSolar + (config.includeEvInRoi ? (totals.evPetrolSavings ?? 0) : 0)) || 1)) * 100).toFixed(1)}% Savings`}
          color="text-orange-500"
          darkMode={darkMode}
          onClick={() => setKpiModalOpen('Solar Benefit')}
        />
        {config.useEv && userLevel !== 'basic' && (
          <KpiCard
            label="EV Cost / 100km"
            value={config.useEv ? averages.evCostPer100km : 0}
            secondary={`${config.evEfficiency} kWh efficiency`}
            color="text-teal-500"
            darkMode={darkMode}
            onClick={() => setKpiModalOpen('EV Cost / 100km')}
          />
        )}
        {config.useEv && (
          <KpiCard
            label="Monthly Fuel Cost"
            value={totals.evCost / 12}
            secondary={`Based on ${config.evDistance} km ${config.evDistMode} commute`}
            darkMode={darkMode}
            onClick={() => setKpiModalOpen('Monthly Fuel Cost')}
          />
        )}
        {showRoiCards && (
          <KpiCard
            label="Total Investment"
            value={Math.round(roi.totalCapitalCost)}
            secondary={
              config.useSolar !== false
                ? config.useEv && config.includeEvInRoi
                  ? 'Solar + EV capital'
                  : 'Solar/Battery capital'
                : config.useEv && config.includeEvInRoi
                  ? 'EV upfront capital'
                  : 'No active investment'
            }
            darkMode={darkMode}
            onClick={() => setKpiModalOpen('Total Investment')}
          />
        )}
        {userLevel !== 'basic' && (
          <KpiCard
            label="Grid Dependency"
            value={`${Math.round(totals.gridKwh).toLocaleString()} kWh`}
            secondary="Imported"
            color="text-amber-500"
            darkMode={darkMode}
            onClick={() => setKpiModalOpen('Grid Dependency')}
          />
        )}
        {showRoiCards && (
          <KpiCard
            label="ROI Payback"
            value={
              config.useSolar === false && !(config.useEv && config.includeEvInRoi)
                ? 'N/A'
                : roi.years === Infinity || roi.years < 0
                  ? 'Never'
                  : roi.years >= 25
                    ? '25+ Years'
                    : `${roi.years.toFixed(1)} Years`
            }
            secondary={
              roi.years === Infinity || roi.years < 0
                ? 'Savings are negative'
                : `Based on $${Math.round(totals.solarBenefit + (totals.evPetrolSavings ?? 0)).toLocaleString()} / yr`
            }
            darkMode={darkMode}
            onClick={() => setKpiModalOpen('ROI Payback')}
          />
        )}
      </div>
    </div>
  )
}
